import { useEffect, useState } from 'react'
import { Loader2, Building2, Scale } from 'lucide-react'
import Modal from '../ui/Modal'
import { api, ApiError } from '../../lib/api'
import type {
  CaseListItem,
  CaseListResponse,
  ClientListItem,
  ClientListResponse,
} from '@toga/shared'

interface Props {
  emailId: string
  subject: string | null
  onClose: () => void
  onArchived: () => void
}

type Mode = 'CLIENT' | 'CASE'

export default function ArchiveEmailModal({ emailId, subject, onClose, onArchived }: Props) {
  const [mode, setMode] = useState<Mode>('CASE')
  const [cases, setCases] = useState<CaseListItem[] | null>(null)
  const [clients, setClients] = useState<ClientListItem[] | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const params = new URLSearchParams()
        if (query.trim()) params.set('q', query.trim())
        params.set('pageSize', '50')
        if (mode === 'CASE') {
          const resp = await api.get<CaseListResponse>(`/cases?${params.toString()}`)
          setCases(resp.items)
          if (resp.items[0]) setSelectedId(resp.items[0].id)
        } else {
          const resp = await api.get<ClientListResponse>(`/clients?${params.toString()}`)
          setClients(resp.items)
          if (resp.items[0]) setSelectedId(resp.items[0].id)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar')
      }
    })()
  }, [mode, query])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedId) return setError('Selecciona un destino')
    setBusy(true)
    setError(null)
    try {
      await api.post(`/emails/${emailId}/archive`, {
        [mode === 'CASE' ? 'caseId' : 'clientId']: selectedId,
      })
      onArchived()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError('Error de red')
      }
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40'

  return (
    <Modal
      open
      onClose={onClose}
      title="Archivar correo"
      description={subject ?? undefined}
      maxWidth="max-w-lg"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setMode('CASE')
              setSelectedId('')
            }}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-2',
              mode === 'CASE'
                ? 'bg-rose-500/15 border-rose-500/40 text-rose-200'
                : 'bg-slate-900/40 border-slate-700/50 t-secondary',
            ].join(' ')}
          >
            <Scale size={14} /> En un expediente
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('CLIENT')
              setSelectedId('')
            }}
            className={[
              'flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-2',
              mode === 'CLIENT'
                ? 'bg-purple-500/15 border-purple-500/40 text-purple-200'
                : 'bg-slate-900/40 border-slate-700/50 t-secondary',
            ].join(' ')}
          >
            <Building2 size={14} /> En un cliente
          </button>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">Buscar</label>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === 'CASE' ? 'Código o título…' : 'Nombre o NIF…'}
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">
            {mode === 'CASE' ? 'Expediente' : 'Cliente'}
          </label>
          {(mode === 'CASE' ? cases : clients) === null ? (
            <div className="t-muted text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Cargando…
            </div>
          ) : (
            <select
              required
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className={inputCls}
              size={6}
            >
              {mode === 'CASE'
                ? cases!.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} · {c.title} · {c.clientDisplayName}
                    </option>
                  ))
                : clients!.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                      {c.nationalId ? ` · ${c.nationalId}` : ''}
                    </option>
                  ))}
            </select>
          )}
        </div>

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || !selectedId}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Archivar
          </button>
        </div>
      </form>
    </Modal>
  )
}
